import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData } from '../services/database';
import { auth, db } from '../services/firebase';
import { doc, setDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';
import ArchieChat from './ArchieChat';
import { useModal } from '../contexts/ModalContext';

const PlannerFormPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useModal();
  const [formData, setFormData] = useState({
    subject: '',
    yearGroup: '',
    topic: '',
    duration: '',
    numberOfActivities: '',
    learningObjectives: '',
    specialRequirements: '',
    customSubject: '',
    customYearGroup: ''
  });

  const subjects = [
    'Mathematics', 'English', 'Science', 'History', 'Geography', 'Art', 'Music', 
    'PE/Sports', 'Computing/IT', 'Design Technology', 'Modern Foreign Languages',
    'Religious Education', 'PSHE', 'Drama', 'Business Studies', 'Economics', 'Other'
  ];

  const yearGroups = [
    'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 
    'Year 12', 'Year 13', 'Primary KS1', 'Primary KS2', 'Other'
  ];

  const durations = ['30 minutes', '45 minutes', '60 minutes', '75 minutes', '90 minutes', '120 minutes'];

  const activityOptions = [
    { value: '1', label: '1 Activity (Short/Simple lesson)' },
    { value: '2', label: '2 Activities (Standard lesson)' },
    { value: '3', label: '3 Activities (Detailed lesson)' },
    { value: '4', label: '4 Activities (Complex/Long lesson)' }
  ];

  const getSuggestedActivities = (duration) => {
    if (duration.includes('30')) return '1-2';
    if (duration.includes('45')) return '2';
    if (duration.includes('60')) return '2-3';
    if (duration.includes('75') || duration.includes('90')) return '3-4';
    if (duration.includes('120')) return '4';
    return '2-3';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updatePlannerUsage = async () => {
    if (!currentUser) {
      console.log('❌ No current user for usage tracking');
      return;
    }

    try {
      console.log('📊 Starting planner usage tracking...');
      console.log('Current user ID:', currentUser.uid);
      
      const currentUserData = await getUserData(currentUser.uid);
      console.log('Current user data:', currentUserData);
      
      if (currentUserData) {
        const currentPlannerUsage = currentUserData.plannerUsage || 0;
        console.log(`Current planner usage: ${currentPlannerUsage}`);
        
        const updatedData = {
          ...currentUserData,
          plannerUsage: currentPlannerUsage + 1,
          lastPlanning: new Date().toISOString()
        };
        
        console.log(`Updating planner usage: ${currentPlannerUsage} → ${currentPlannerUsage + 1}`);
        
        await setDoc(doc(db, 'users', currentUser.uid), updatedData);
        
        console.log('✅ Planner usage updated successfully!');
        console.log(`New planner usage: ${currentPlannerUsage + 1}`);
      } else {
        console.log('❌ No user data found for usage tracking');
      }
    } catch (usageError) {
      console.error('❌ Failed to update planner usage tracking:', usageError);
      console.error('Usage error details:', {
        message: usageError.message,
        stack: usageError.stack
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.yearGroup || !formData.topic || !formData.duration) {
      await showAlert('Please fill in all required fields', 'Missing Fields');
      return;
    }

    if (formData.subject === 'Other' && !formData.customSubject.trim()) {
      await showAlert('Please specify the subject', 'Missing Fields');
      return;
    }

    if (formData.yearGroup === 'Other' && !formData.customYearGroup.trim()) {
      await showAlert('Please specify the year group', 'Missing Fields');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🤖 Starting lesson plan generation...');
      console.log('Form data:', formData);
      
      const response = await fetch('/api/plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: formData.subject === 'Other' ? formData.customSubject : formData.subject,
          yearGroup: formData.yearGroup === 'Other' ? formData.customYearGroup : formData.yearGroup,
        }),
      });

      console.log('📡 API response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to generate lesson plan');
      }

      const result = await response.json();
      console.log('✅ Lesson plan generated successfully!');
      
      console.log('📊 Updating planner usage tracking...');
      await updatePlannerUsage();
      
      navigate('/planner/result', { state: { lessonPlan: result.lessonPlan, formData } });
      
    } catch (error) {
      console.error('❌ Error generating lesson plan:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      await showAlert('Sorry, there was an error generating your lesson plan. Please try again.', 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/planner');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="flex items-center justify-center py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              Plan Your Lesson
            </h1>
            <p className="text-lg text-ink-700">Tell us about your lesson and we'll create a complete plan with differentiation</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Required Fields Row */}
              <div className="grid md:grid-cols-2 gap-6">
                
                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                  
                  {/* Custom Subject Field */}
                  {formData.subject === 'Other' && (
                    <input
                      type="text"
                      name="customSubject"
                      value={formData.customSubject}
                      onChange={handleInputChange}
                      placeholder="Please specify the subject"
                      className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent mt-3"
                      required
                    />
                  )}
                </div>

                {/* Year Group */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Year Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="yearGroup"
                    value={formData.yearGroup}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select year group</option>
                    {yearGroups.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  
                  {/* Custom Year Group Field */}
                  {formData.yearGroup === 'Other' && (
                    <input
                      type="text"
                      name="customYearGroup"
                      value={formData.customYearGroup}
                      onChange={handleInputChange}
                      placeholder="Please specify the year group"
                      className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent mt-3"
                      required
                    />
                  )}
                </div>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Lesson Topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  placeholder="e.g., Introduction to Fractions, World War 1 Causes, Plant Photosynthesis"
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Duration and Number of Activities Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Duration */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Lesson Duration <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select duration</option>
                    {durations.map(duration => (
                      <option key={duration} value={duration}>{duration}</option>
                    ))}
                  </select>
                </div>

                {/* Number of Activities */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Number of Main Activities <span className="text-ink-400">(Optional)</span>
                  </label>
                  <select
                    name="numberOfActivities"
                    value={formData.numberOfActivities}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  >
                    <option value="">Let AI decide based on lesson content</option>
                    {activityOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  
                  {/* Helpful suggestion based on duration */}
                  {formData.duration && !formData.numberOfActivities && (
                    <p className="text-sm text-coral-600 mt-2">
                      💡 AI will suggest {getSuggestedActivities(formData.duration)} activities for {formData.duration}, but can adjust based on lesson complexity
                    </p>
                  )}
                  
                  {/* Show selection confirmation */}
                  {formData.numberOfActivities && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ AI will create exactly {formData.numberOfActivities} activities
                    </p>
                  )}
                </div>
              </div>

              {/* Optional Fields */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-ink-800 mb-4">Optional Details</h3>
                
                {/* Learning Objectives */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Learning Objectives
                  </label>
                  <textarea
                    name="learningObjectives"
                    value={formData.learningObjectives}
                    onChange={handleInputChange}
                    placeholder="Leave blank for AI suggestions, or specify your learning objectives..."
                    rows="3"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Special Requirements */}
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-2">
                    Special Requirements
                  </label>
                  <textarea
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleInputChange}
                    placeholder="e.g., SEN considerations, EAL support, specific resources available, class behaviour notes..."
                    rows="3"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-6 border-t">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-colors font-medium"
                >
                  ← Back to Planner
                </button>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    isLoading 
                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                      : 'bg-coral-300 text-white hover:bg-coral-500 transform hover:-translate-y-0.5'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Your Lesson Plan...
                    </div>
                  ) : (
                    'Generate Lesson Plan →'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Usage Info */}
          <div className="mt-6 text-center">
            <div className="bg-white bg-opacity-20 rounded-lg border-4 border-ink-900 p-4 max-w-xl mx-auto">
              <p className="text-ink-900 text-sm">
                <span className="font-semibold">✨ Each lesson includes:</span> Built-in differentiation, detailed activities, assessment strategies, and downloadable PDF document
              </p>
            </div>
          </div>
        </div>
      </div>
      <ArchieChat />
    </div>
  );
};

export default PlannerFormPage;